import { useMemo } from 'react';

import { useFishGroupsController_getRevertPreview } from '@/services/hooks';
import type { RevertPreviewDto } from '@/services/types';

import type { DomainQueryOptions } from '../shared/types';

import { isRevertPreviewDto, mapRevertPreviewToDomain } from './revertMappers';
import type { UseGetRevertPreviewResult } from './revertTypes';

/**
 * What reverting `operationId` would do, and what stands in the way. Read fresh
 * every time the dialog opens: it describes the current state of the groups, so a
 * cached answer could offer a revert that is no longer possible.
 */
export const useGetRevertPreview = (
    clientId: number,
    operationId: number,
    options?: DomainQueryOptions<RevertPreviewDto>
): UseGetRevertPreviewResult => {
    const query = useFishGroupsController_getRevertPreview(clientId, operationId, {
        gcTime: 0,
        staleTime: 0,
        ...options,
    });

    const preview = useMemo(() => {
        // The generated client only casts the body, so check it before mapping.
        const data: unknown = query.data?.data;
        return isRevertPreviewDto(data) ? mapRevertPreviewToDomain(data) : null;
    }, [query.data?.data]);

    return {
        data: preview,
        isLoading: query.isPending,
        isFetching: query.isFetching,
        isFetched: query.isFetched,
        error: query.error,
        refetch: query.refetch,
    };
};
